import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Tab, Tabs, Badge } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import AdminNavbar from '../components/AdminNavbar';
import PreviousSessionResources from '../components/PreviousSessionResources';
import ReadAIAccountConfig from '../components/ReadAIAccountConfig';
import { SOUTH_AFRICAN_PROVINCES } from '../constants/addressConstants';
import { UI_TIMEOUTS } from '../constants/ui';

const AdminProfile = () => {
  const { currentUser, userProfile, fetchUserProfile } = useAuth();
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    phoneNumber: '',
    department: '',
    jobTitle: '',
    bio: '',
    streetAddress: '',
    city: '',
    province: '',
    postalCode: '',
    preferences: {
      emailNotifications: true,
      pushNotifications: true,
      weeklyReports: true,
      systemAlerts: true
    }
  });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (userProfile) {
      setFormData({
        displayName: userProfile.displayName || '',
        email: userProfile.email || '',
        phoneNumber: userProfile.phoneNumber || '',
        department: userProfile.department || '',
        jobTitle: userProfile.jobTitle || '',
        bio: userProfile.bio || '',
        streetAddress: userProfile.streetAddress || '',
        city: userProfile.city || '',
        province: userProfile.province || '',
        postalCode: userProfile.postalCode || '',
        preferences: {
          emailNotifications: userProfile.preferences?.emailNotifications ?? true,
          pushNotifications: userProfile.preferences?.pushNotifications ?? true,
          weeklyReports: userProfile.preferences?.weeklyReports ?? true,
          systemAlerts: userProfile.preferences?.systemAlerts ?? true
        }
      });
    }
  }, [userProfile]);

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('preferences.')) {
      const prefName = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          [prefName]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {
        ...formData,
        updatedAt: serverTimestamp()
      };

      await updateDoc(doc(db, 'users', currentUser.uid), updateData);
      await fetchUserProfile(currentUser);
      
      showAlert('Profile updated successfully!', 'success');
    } catch (error) {
      devError('Error updating profile:', error);
      showAlert('Error updating profile', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showAlert('New passwords do not match', 'danger');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      showAlert('Password must be at least 8 characters long', 'danger');
      return;
    }

    setLoading(true);

    try {
      // In a real implementation, you would use Firebase Auth to update the password
      // This is a placeholder for the password update logic
      showAlert('Password change functionality would be implemented here with Firebase Auth', 'info');
      
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      devError('Error updating password:', error);
      showAlert('Error updating password', 'danger');
    } finally {
      setLoading(false);
    }
  };

  if (userProfile?.role !== 'admin') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access this page.</p>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <AdminNavbar currentPage="profile" />
      
      <Container className="mt-4">
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1>Admin Profile</h1>
                <p className="text-muted">Manage your profile information and preferences</p>
              </div>
              <Badge bg="primary" className="fs-6">Administrator</Badge>
            </div>
          </Col>
        </Row>

        {alert.show && (
          <Alert variant={alert.type} className="mb-4">
            {alert.message}
          </Alert>
        )}

        <Row>
          <Col md={8}>
            <Tabs defaultActiveKey="profile" className="mb-4">
              <Tab eventKey="profile" title="Profile Information">
                <Card>
                  <Card.Header>
                    <h5>Personal Information</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form onSubmit={handleProfileUpdate}>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Display Name *</Form.Label>
                            <Form.Control
                              type="text"
                              name="displayName"
                              value={formData.displayName}
                              onChange={handleInputChange}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Email Address *</Form.Label>
                            <Form.Control
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              required
                              disabled // Email usually can't be changed directly
                            />
                            <Form.Text className="text-muted">
                              Contact support to change your email address
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Phone Number</Form.Label>
                            <Form.Control
                              type="tel"
                              name="phoneNumber"
                              value={formData.phoneNumber}
                              onChange={handleInputChange}
                              placeholder="+27 12 345 6789"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Department</Form.Label>
                            <Form.Control
                              type="text"
                              name="department"
                              value={formData.department}
                              onChange={handleInputChange}
                              placeholder="e.g., IT Administration"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-3">
                        <Form.Label>Job Title</Form.Label>
                        <Form.Control
                          type="text"
                          name="jobTitle"
                          value={formData.jobTitle}
                          onChange={handleInputChange}
                          placeholder="e.g., System Administrator"
                        />
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label>Bio</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="bio"
                          value={formData.bio}
                          onChange={handleInputChange}
                          placeholder="Tell us about yourself..."
                        />
                      </Form.Group>

                      <hr className="my-4" />
                      <h6 className="mb-3">Address Information</h6>

                      <Form.Group className="mb-3">
                        <Form.Label>Street Address</Form.Label>
                        <Form.Control
                          type="text"
                          name="streetAddress"
                          value={formData.streetAddress}
                          onChange={handleInputChange}
                          placeholder="Enter street address"
                        />
                      </Form.Group>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>City</Form.Label>
                            <Form.Control
                              type="text"
                              name="city"
                              value={formData.city}
                              onChange={handleInputChange}
                              placeholder="Enter city"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Province</Form.Label>
                            <Form.Select
                              name="province"
                              value={formData.province}
                              onChange={handleInputChange}
                            >
                              <option value="">Select province...</option>
                              {SOUTH_AFRICAN_PROVINCES.map(prov => (
                                <option key={prov} value={prov}>{prov}</option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-4">
                            <Form.Label>Postal Code</Form.Label>
                            <Form.Control
                              type="text"
                              name="postalCode"
                              value={formData.postalCode}
                              onChange={handleInputChange}
                              placeholder="4 digits"
                              maxLength={4}
                              pattern="\d{4}"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Button
                        variant="primary"
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? 'Updating...' : 'Update Profile'}
                      </Button>
                    </Form>
                  </Card.Body>
                </Card>
              </Tab>

              <Tab eventKey="preferences" title="Preferences">
                <Card>
                  <Card.Header>
                    <h5>⚙️ Notification Preferences</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          name="preferences.emailNotifications"
                          label="Email Notifications"
                          checked={formData.preferences.emailNotifications}
                          onChange={handleInputChange}
                        />
                        <Form.Text className="text-muted">
                          Receive email notifications for important system events
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          name="preferences.pushNotifications"
                          label="Push Notifications"
                          checked={formData.preferences.pushNotifications}
                          onChange={handleInputChange}
                        />
                        <Form.Text className="text-muted">
                          Receive browser push notifications
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          name="preferences.weeklyReports"
                          label="Weekly Reports"
                          checked={formData.preferences.weeklyReports}
                          onChange={handleInputChange}
                        />
                        <Form.Text className="text-muted">
                          Receive weekly system and user activity reports
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Check
                          type="checkbox"
                          name="preferences.systemAlerts"
                          label="System Alerts"
                          checked={formData.preferences.systemAlerts}
                          onChange={handleInputChange}
                        />
                        <Form.Text className="text-muted">
                          Receive alerts for system errors and maintenance
                        </Form.Text>
                      </Form.Group>

                      <Button 
                        variant="primary" 
                        onClick={handleProfileUpdate}
                        disabled={loading}
                      >
                        {loading ? 'Saving...' : 'Save Preferences'}
                      </Button>
                    </Form>
                  </Card.Body>
                </Card>
              </Tab>

              <Tab eventKey="security" title="Security">
                <Card>
                  <Card.Header>
                    <h5>🔒 Change Password</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form onSubmit={handlePasswordUpdate}>
                      <Form.Group className="mb-3">
                        <Form.Label>Current Password *</Form.Label>
                        <Form.Control
                          type="password"
                          name="currentPassword"
                          value={passwordData.currentPassword}
                          onChange={handlePasswordChange}
                          required
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>New Password *</Form.Label>
                        <Form.Control
                          type="password"
                          name="newPassword"
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange}
                          required
                          minLength={8}
                        />
                        <Form.Text className="text-muted">
                          Must be at least 8 characters long
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label>Confirm New Password *</Form.Label>
                        <Form.Control
                          type="password"
                          name="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange}
                          required
                          minLength={8}
                        />
                      </Form.Group>

                      <Button
                        variant="warning"
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? 'Updating...' : 'Change Password'}
                      </Button>
                    </Form>

                    <hr className="my-4" />

                    <div>
                      <h6>Security Information</h6>
                      <p className="text-muted mb-2">
                        <strong>Last Login:</strong> {currentUser?.metadata?.lastSignInTime || 'Unknown'}
                      </p>
                      <p className="text-muted">
                        <strong>Account Created:</strong> {currentUser?.metadata?.creationTime || 'Unknown'}
                      </p>
                    </div>
                  </Card.Body>
                </Card>
              </Tab>

              <Tab eventKey="session-resources" title="📹 Previous Session Resources">
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">📹 Previous Session Resources</h5>
                    <p className="text-muted mb-0 small">
                      Manage meeting recordings and notes from ReadAI
                    </p>
                  </Card.Header>
                  <Card.Body>
                    <PreviousSessionResources />
                  </Card.Body>
                </Card>
              </Tab>

              <Tab eventKey="readai-config" title="⚙️ ReadAI Configuration">
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">⚙️ ReadAI Integration Configuration</h5>
                    <p className="text-muted mb-0 small">
                      Configure your ReadAI account and integration settings
                    </p>
                  </Card.Header>
                  <Card.Body>
                    <ReadAIAccountConfig />
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          </Col>

          <Col md={4}>
            <Card>
              <Card.Header>
                <h5>Account Summary</h5>
              </Card.Header>
              <Card.Body>
                <div className="text-center mb-3">
                  <div className="bg-primary rounded-circle d-inline-flex align-items-center justify-content-center" 
                       style={{ width: '80px', height: '80px', fontSize: '2rem' }}>
                    {formData.displayName ? formData.displayName.charAt(0).toUpperCase() : '👤'}
                  </div>
                  <h5 className="mt-2">{formData.displayName}</h5>
                  <Badge bg="primary">Administrator</Badge>
                </div>

                <hr />

                <div className="mb-3">
                  <strong>Email:</strong>
                  <div className="text-muted">{formData.email}</div>
                </div>

                {formData.department && (
                  <div className="mb-3">
                    <strong>Department:</strong>
                    <div className="text-muted">{formData.department}</div>
                  </div>
                )}

                {formData.jobTitle && (
                  <div className="mb-3">
                    <strong>Job Title:</strong>
                    <div className="text-muted">{formData.jobTitle}</div>
                  </div>
                )}

                <hr />

                <div>
                  <strong>Permissions:</strong>
                  <div className="mt-2">
                    <Badge bg="success" className="me-1">User Management</Badge>
                    <Badge bg="success" className="me-1">System Admin</Badge>
                    <Badge bg="success" className="me-1">Reports</Badge>
                    <Badge bg="success" className="me-1">Full Access</Badge>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card className="mt-4">
              <Card.Header>
                <h5>Quick Actions</h5>
              </Card.Header>
              <Card.Body>
                <div className="d-grid gap-2">
                  <Button variant="outline-primary" size="sm">
                    View System Analytics
                  </Button>
                  <Button variant="outline-success" size="sm">
                    Manage Users
                  </Button>
                  <Button variant="outline-info" size="sm">
                    Generate Reports
                  </Button>
                  <Button variant="outline-warning" size="sm">
                    System Settings
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AdminProfile;